import React, { useState, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { ArrowUpRight, Upload, X, FileText, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const inputStyle = {
  width: '100%',
  padding: '14px 16px',
  background: 'rgba(255, 255, 255, 0.04)',
  border: '1px solid rgba(255, 255, 255, 0.12)',
  borderRadius: '0px',
  color: 'var(--text-primary)',
  fontSize: '15px',
  fontFamily: "'Inter', sans-serif",
  outline: 'none',
  transition: 'border-color 0.3s ease',
};

export const ContactModal = ({ open, onOpenChange, inquiryType }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: '',
  });
  const [file, setFile] = useState(null);
  const [sending, setSending] = useState(false);
  const fileInputRef = useRef(null);

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleFileChange = (e) => {
    const selected = e.target.files[0];
    if (selected) {
      const maxSize = 25 * 1024 * 1024; // 25MB
      if (selected.size > maxSize) {
        toast.error('File size must be under 25MB.');
        return;
      }
      setFile(selected);
    }
  };

  const removeFile = () => {
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email) {
      toast.error('Name and email are required.');
      return;
    }

    setSending(true);
    try {
      const data = new FormData();
      data.append('name', formData.name);
      data.append('email', formData.email);
      data.append('company', formData.company);
      data.append('message', formData.message);
      data.append('inquiry_type', inquiryType || 'General');
      if (file) {
        data.append('file', file);
      }

      await axios.post(`${API}/contact`, data, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      toast.success('Message sent successfully. We\'ll be in touch.');
      setFormData({ name: '', email: '', company: '', message: '' });
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      onOpenChange(false);
    } catch (err) {
      console.error(err);
      toast.error('Something went wrong. Please try again or email us directly.');
    } finally {
      setSending(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[540px] p-0 gap-0 border-0"
        style={{
          background: '#0a0a0a',
          border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: '0px',
          maxHeight: '90vh',
          overflowY: 'auto',
        }}
      >
        <DialogHeader style={{ padding: '32px 32px 0' }}>
          <DialogTitle
            style={{
              fontSize: '24px',
              fontWeight: 600,
              color: 'var(--text-primary)',
              fontFamily: "'Inter', sans-serif",
              marginBottom: '8px',
            }}
          >
            {inquiryType || 'Get in Touch'}
          </DialogTitle>
          <DialogDescription
            style={{
              fontSize: '14px',
              color: 'var(--text-muted)',
              fontFamily: "'Inter', sans-serif",
            }}
          >
            Fill in the details below and we'll respond within 48 hours.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} style={{ padding: '24px 32px 32px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {/* Name + Email row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label
                  style={{
                    display: 'block',
                    fontSize: '12px',
                    fontWeight: 500,
                    color: 'var(--text-muted)',
                    marginBottom: '6px',
                    textTransform: 'uppercase',
                    letterSpacing: '1px',
                    fontFamily: "'Inter', sans-serif",
                  }}
                >
                  Name *
                </label>
                <input
                  name="name"
                  type="text"
                  placeholder="Full name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  style={inputStyle}
                  onFocus={(e) => { e.target.style.borderColor = 'var(--brand-primary)'; }}
                  onBlur={(e) => { e.target.style.borderColor = 'rgba(255,255,255,0.12)'; }}
                />
              </div>
              <div>
                <label
                  style={{
                    display: 'block',
                    fontSize: '12px',
                    fontWeight: 500,
                    color: 'var(--text-muted)',
                    marginBottom: '6px',
                    textTransform: 'uppercase',
                    letterSpacing: '1px',
                    fontFamily: "'Inter', sans-serif",
                  }}
                >
                  Email *
                </label>
                <input
                  name="email"
                  type="email"
                  placeholder="you@company.com"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  style={inputStyle}
                  onFocus={(e) => { e.target.style.borderColor = 'var(--brand-primary)'; }}
                  onBlur={(e) => { e.target.style.borderColor = 'rgba(255,255,255,0.12)'; }}
                />
              </div>
            </div>

            {/* Company */}
            <div>
              <label
                style={{
                  display: 'block',
                  fontSize: '12px',
                  fontWeight: 500,
                  color: 'var(--text-muted)',
                  marginBottom: '6px',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  fontFamily: "'Inter', sans-serif",
                }}
              >
                Company
              </label>
              <input
                name="company"
                type="text"
                placeholder="Company or fund name"
                value={formData.company}
                onChange={handleChange}
                style={inputStyle}
                onFocus={(e) => { e.target.style.borderColor = 'var(--brand-primary)'; }}
                onBlur={(e) => { e.target.style.borderColor = 'rgba(255,255,255,0.12)'; }}
              />
            </div>

            {/* Message */}
            <div>
              <label
                style={{
                  display: 'block',
                  fontSize: '12px',
                  fontWeight: 500,
                  color: 'var(--text-muted)',
                  marginBottom: '6px',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  fontFamily: "'Inter', sans-serif",
                }}
              >
                Message
              </label>
              <textarea
                name="message"
                placeholder="Tell us about your interest..."
                value={formData.message}
                onChange={handleChange}
                rows={4}
                style={{ ...inputStyle, resize: 'vertical', minHeight: '100px' }}
                onFocus={(e) => { e.target.style.borderColor = 'var(--brand-primary)'; }}
                onBlur={(e) => { e.target.style.borderColor = 'rgba(255,255,255,0.12)'; }}
              />
            </div>

            {/* File Upload */}
            <div>
              <label
                style={{
                  display: 'block',
                  fontSize: '12px',
                  fontWeight: 500,
                  color: 'var(--text-muted)',
                  marginBottom: '6px',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  fontFamily: "'Inter', sans-serif",
                }}
              >
                Attach Deck / Documents
              </label>

              {!file ? (
                <div
                  onClick={() => fileInputRef.current?.click()}
                  style={{
                    padding: '20px',
                    border: '1px dashed rgba(255,255,255,0.15)',
                    background: 'rgba(255,255,255,0.02)',
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '8px',
                    transition: 'border-color 0.3s ease, background 0.3s ease',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = 'var(--brand-primary)';
                    e.currentTarget.style.background = 'rgba(0,255,209,0.02)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(255,255,255,0.15)';
                    e.currentTarget.style.background = 'rgba(255,255,255,0.02)';
                  }}
                >
                  <Upload size={20} style={{ color: 'var(--text-muted)' }} />
                  <span style={{ fontSize: '13px', color: 'var(--text-muted)', fontFamily: "'Inter', sans-serif" }}>
                    Click to upload (PDF, PPTX, DOC — max 25MB)
                  </span>
                </div>
              ) : (
                <div
                  style={{
                    padding: '12px 16px',
                    background: 'rgba(0, 255, 209, 0.04)',
                    border: '1px solid rgba(0, 255, 209, 0.15)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '12px',
                  }}
                >
                  <div className="flex items-center gap-3" style={{ minWidth: 0 }}>
                    <FileText size={18} style={{ color: 'var(--brand-primary)', flexShrink: 0 }} />
                    <div style={{ minWidth: 0 }}>
                      <span
                        style={{
                          fontSize: '14px',
                          color: 'var(--text-primary)',
                          fontFamily: "'Inter', sans-serif",
                          display: 'block',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {file.name}
                      </span>
                      <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                        {formatFileSize(file.size)}
                      </span>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={removeFile}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: 'var(--text-muted)',
                      padding: '4px',
                      flexShrink: 0,
                    }}
                  >
                    <X size={16} />
                  </button>
                </div>
              )}

              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.pptx,.ppt,.doc,.docx,.xlsx,.xls,.txt,.csv"
                onChange={handleFileChange}
                style={{ display: 'none' }}
              />
            </div>

            {/* Submit */}
            <button
              type="submit"
              className="oa-btn-primary"
              disabled={sending}
              style={{
                width: '100%',
                justifyContent: 'center',
                marginTop: '8px',
                opacity: sending ? 0.6 : 1,
              }}
            >
              {sending ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  Send Message
                  <ArrowUpRight size={16} />
                </>
              )}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
